// Order status helpers — combine the two-dimension (stage, state) enums
// into a single human label and a brand-aligned StatusPill tone.
//
// Status taxonomy (per apps/api/src/db/schema/enums.ts):
//   stage: quote · committed · in_production · shipped · invoiced · closed · cancelled
//   state: draft · sent · viewed · approved · changes_requested · cancelled · paid

// Mirror of StatusPill's tone variants — kept in sync manually since
// StatusPillProps isn't exported. If you add a tone in StatusPill.tsx,
// add it here too.
type Tone = "active" | "prospect" | "dormant" | "closed" | "warn" | "info";

/** Filter chip choices for the Orders list page. PR #140 reframes the
 *  list as "Quotes" — default chip is `sales-orders` which excludes
 *  estimates (statusStage='quote'). Reps who DO want estimates click the
 *  Estimates chip explicitly. */
export const ORDER_STATUS_FILTERS: ReadonlyArray<{
  value: OrderStatusGroup | "all";
  label: string;
  /** When set, restricts the API query to these stages. undefined = no restriction. */
  stages?: ReadonlyArray<string>;
}> = [
  {
    value: "sales-orders",
    label: "Quotes",
    stages: ["committed", "in_production", "shipped", "invoiced", "closed", "cancelled"],
  },
  { value: "quote", label: "Estimates", stages: ["quote"] },
  {
    value: "active",
    label: "Active",
    stages: ["committed", "in_production"],
  },
  {
    value: "fulfilled",
    label: "Shipped / Invoiced",
    stages: ["shipped", "invoiced"],
  },
  { value: "closed", label: "Closed", stages: ["closed", "cancelled"] },
  { value: "all", label: "All" },
];

export type OrderStatusGroup =
  | "sales-orders"
  | "quote"
  | "active"
  | "fulfilled"
  | "closed";

/** Combined human label, e.g. "Quote — Sent" or "In Production".
 *
 *  `opts.omitStagePrefix` — when the surrounding surface already names the
 *  document (e.g. an "Estimate" eyebrow on OrderDetailPage), the "Quote —"
 *  prefix on a quote-stage label just repeats it. Passing this drops the
 *  prefix and returns the state alone ("Sent", "Approved"). Default (unset)
 *  keeps the full label so mixed lists like OrdersListPage stay unambiguous. */
export function orderStatusLabel(
  stage: string,
  state: string,
  opts?: { omitStagePrefix?: boolean },
): string {
  const stageLabel = STAGE_LABELS[stage] ?? stage;
  // For non-quote stages, the state is usually mechanical noise — only show
  // it when meaningful (changes_requested, paid).
  if (stage === "quote") {
    const stateLabel = STATE_LABELS[state] ?? state;
    // Every order is a Quote (CEO 2026-10-15) — the state says where it is.
    return opts?.omitStagePrefix ? stateLabel : `Quote — ${stateLabel}`;
  }
  if (state === "changes_requested") {
    return `${stageLabel} — Changes requested`;
  }
  if (state === "paid" && stage === "invoiced") {
    return "Paid";
  }
  return stageLabel;
}

/** Pill tone — picks one of the StatusPill variants per stage/state. */
export function orderStatusTone(stage: string, state: string): Tone {
  // Cancelled trumps everything.
  if (stage === "cancelled" || state === "cancelled") return "warn";
  // Changes requested = warn (rep needs to act).
  if (state === "changes_requested") return "warn";
  // Quote stage colors based on state.
  if (stage === "quote") {
    if (state === "approved") return "active";
    if (state === "sent" || state === "viewed") return "prospect";
    return "info"; // draft
  }
  // Mid-flow.
  if (stage === "committed" || stage === "in_production") return "active";
  // Done.
  if (stage === "shipped" || stage === "invoiced") return "active";
  if (stage === "closed") return "closed";
  return "info";
}

const STAGE_LABELS: Record<string, string> = {
  quote: "Quote",
  // A live order: the client approved (CEO 2026-10-15 vocabulary).
  committed: "Approved · live",
  in_production: "In Production",
  shipped: "Shipped",
  invoiced: "Invoiced",
  closed: "Closed",
  cancelled: "Cancelled",
};

const STATE_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  approved: "Approved",
  changes_requested: "Changes Requested",
  cancelled: "Cancelled",
  paid: "Paid",
};
